import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

/**
 * Global shopping cart (COD only). Persisted to localStorage so it survives
 * navigation between the in-app map and the public /shop pages.
 */
const CartCtx = createContext(null);
const KEY = "mapbazar_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    if (!product?.id) return;
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id === product.id);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + qty };
        return next;
      }
      const unit = (product.deal_discount_pct > 0 ? product.deal_price : product.price) || product.price || 0;
      return [...prev, {
        id: product.id,
        name: product.name,
        price: unit,
        image: product.image,
        shop_id: product.shop_id,
        shop_name: product.shop_name,
        shop_phone: product.shop_phone || product.shop_whatsapp || "",
        lat: typeof product.lat === "number" ? product.lat : undefined,
        lng: typeof product.lng === "number" ? product.lng : undefined,
        qty,
      }];
    });
  }, []);

  const remove = useCallback((id) => setItems((p) => p.filter((x) => x.id !== id)), []);
  const setQty = useCallback((id, qty) => setItems((p) => p.map((x) => (x.id === id ? { ...x, qty: Math.max(1, qty) } : x))), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((n, x) => n + x.qty, 0);
  const subtotal = items.reduce((s, x) => s + (x.price || 0) * x.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, count, subtotal }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  return useContext(CartCtx) || {
    items: [], add: () => {}, remove: () => {}, setQty: () => {}, clear: () => {}, count: 0, subtotal: 0,
  };
}
